import { useState } from 'react'

import { Avatar, Box, Button, Text, useToggle } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import {
  CommentDetailsFragment,
  DeleteCommentDocument,
  PostDataFragmentDoc,
  UpdateCommentDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CommentInput from 'modules/posts/components/Comments/Input'
import HTMLWithMentions from 'modules/posts/components/HTMLWithMentions'
import { useCommunitiesClapsFeature } from 'modules/posts/hooks/useCommunitiesClapsFeature'

interface DeleteModalProps {
  postId: number
  comment: CommentDetailsFragment
  onClose: () => void
}

const DeleteModal = observer(({ postId, comment, onClose }: DeleteModalProps) => {
  const [deleteComment, { loading }] = useMutation(DeleteCommentDocument, {
    extractorFn: (data) => data.postsMutations.deletePostsComment,
    update: (cache) => {
      cache.updateFragment(
        {
          id: `PostsPost:${postId}`,
          fragment: PostDataFragmentDoc,
          fragmentName: 'PostData',
        },
        (data) => {
          if (data) {
            return {
              ...data,
              commentsCount: {
                totalCount: data.commentsCount.totalCount ? data?.commentsCount.totalCount - 1 : 0,
              },
            }
          }
          return null
        }
      )
      cache.evict({ id: cache.identify(comment) })
      cache.gc()
    },
  })

  return (
    <Modal
      illustration="alert"
      title={i18n.t('posts.comments.delete_comment')}
      description={i18n.t('posts.comments.confirm_delete')}
      onClose={onClose}
      disableFocusTrap // TODO: if this is enabled it hangs the whole application
      onSubmit={async () => {
        const result = await deleteComment({
          variables: {
            id: comment.id,
            postId,
          },
        })

        const handled = handleResult(result, (_errors) => {})

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.removed_successfully'),
          })
          onClose()
        }

        return handled
      }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {i18n.t('posts.comments.proceed')}
          </Button>
        </>
      }
    />
  )
})

interface CommentProps {
  postId: number
  comment: CommentDetailsFragment
  usersIds: number[]
  setUsersIds: (value: number[]) => void
  canManagePosts: boolean
}
interface ActionProps {
  postId: number
  comment: CommentDetailsFragment
  setEditing: (editing: boolean) => void
  userIsOwner: boolean
}

const Actions = ({ postId, comment, setEditing, userIsOwner }: ActionProps) => {
  const [deleteModalOpen, toggleDeleteModal] = useToggle(false)

  return (
    userIsOwner && (
      <>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            <PopoverMenu.TouchableOption
              onClick={() => setEditing(true)}
              title={i18n.t('common.edit')}
            />
            <PopoverMenu.TouchableOption
              onClick={toggleDeleteModal(true)}
              title={i18n.t('common.destroy')}
              danger
            />
          </PopoverMenu.Content>
        </PopoverMenu>
        {deleteModalOpen && (
          <DeleteModal postId={postId} comment={comment} onClose={toggleDeleteModal(false)} />
        )}
      </>
    )
  )
}

const CommentComponent = ({
  postId,
  comment,
  usersIds,
  setUsersIds,
  canManagePosts,
}: CommentProps) => {
  const { text, author, createdAt } = comment
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const [newComment, setComment] = useState<string | null>(text)
  const [editing, setEditing] = useState<boolean>(false)
  const timeAgo = moment(createdAt).fromNow()
  const userIsOwner = currentAccessId === author?.id
  const canUseCommentActions = userIsOwner || canManagePosts
  const [updateComment, { loading }] = useMutation(UpdateCommentDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsComment,
  })

  const hasClaps = useCommunitiesClapsFeature()
  const sanitizeOptions = hasClaps
    ? {
        ALLOWED_TAGS: ['div', 'span', 'strong', 'a'],
        ALLOWED_ATTR: ['class', 'href'],
        ALLOWED_URI_REGEXP: /^\/employees\/\d+$/gim,
      }
    : {}

  const postComment = DOMPurify.sanitize(text, sanitizeOptions)

  return (
    <Box paddingY="s24">
      {editing && author ? (
        <CommentInput
          onSubmit={async () => {
            const result = handleResult(
              await updateComment({
                variables: {
                  id: comment.id,
                  postId: postId,
                  text: newComment ?? '',
                  mentionedAccessIds: usersIds ?? [],
                },
              }),
              (_errors) => {}
            )

            if (result.success) {
              setEditing(false)
              app.setFlash({
                type: 'correct',
                message: i18n.t('common.saved_successfully'),
              })
            }
          }}
          saving={loading}
          setComment={setComment}
          comment={newComment}
          setUsersIds={setUsersIds}
        />
      ) : (
        <Box width="full" gap="s24" flexDirection="row">
          <Avatar url={buildUrl(author?.avatar)} label={author?.fullName ?? ''} size="s48" />
          <Box flexDirection="column" width="full">
            <Box
              width="full"
              component="li"
              background="grey200"
              borderRadius={{ all: 'abs008' }}
              gap="s8"
              paddingX="s24"
              paddingY="s16"
              flexDirection="row"
              justifyContent="spaceBetween"
            >
              {postComment && (
                <Box justifyContent="center">
                  <Text.Normal whiteSpace="breakSpaces" overflowWrap>
                    {hasClaps && <HTMLWithMentions html={postComment} />}
                    {!hasClaps && <div>{postComment}</div>}
                  </Text.Normal>
                </Box>
              )}
              {canUseCommentActions && (
                <Box>
                  <Actions
                    comment={comment}
                    setEditing={setEditing}
                    postId={postId}
                    userIsOwner={userIsOwner}
                  />
                </Box>
              )}
            </Box>
            <Text.Normal color="grey800" size="200">
              {author?.fullName}
              {', '}
              {timeAgo}
            </Text.Normal>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default observer(CommentComponent)
